import { PAGES, S3File } from "@dune/types";
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
	Button,
	Card,
	ControlledInput,
	ControlledPassword,
	ControlledPhoneInput,
} from "@dune/ui";
import { getInitials } from "@dune/utils";
import { LogOut04, Trash02 } from "@untitled-ui/icons-react";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
	useChangePassword,
	useUpdateProfile,
} from "../../api/mutations/users.mutations";
import { getPrivateFileUrl } from "../../api/queries/files.queries";
import { useGetUserProfile } from "../../api/queries/users.queries";
import { ControlledS3FileInput } from "../../component/form/controlledS3Input";
import { useAuthStore } from "../../stores/auth.store";

interface ProfileFormData {
	firstname: string;
	lastname: string;
	email: string;
	mobilePhone: string;
	profilePicture?: S3File;
}

interface PasswordFormData {
	currentPassword: string;
	newPassword: string;
	confirmPassword: string;
}

const ProfilePage: React.FC = () => {
	const { data: userProfile, isLoading } = useGetUserProfile();
	const logout = useAuthStore((state) => state.logout);
	const navigate = useNavigate();
	const updateProfileMutation = useUpdateProfile();
	const changePasswordMutation = useChangePassword();

	const [previewFile, setPreviewFile] = useState<S3File | undefined>(undefined);

	const profileForm = useForm<ProfileFormData>({
		defaultValues: {
			firstname: "",
			lastname: "",
			email: "",
			mobilePhone: "",
			profilePicture: undefined,
		},
	});

	const passwordForm = useForm<PasswordFormData>({
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	});

	useEffect(() => {
		if (userProfile) {
			profileForm.reset({
				firstname: userProfile.firstname || "",
				lastname: userProfile.lastname || "",
				email: userProfile.email || "",
				mobilePhone: userProfile.mobilePhone || "",
				profilePicture: undefined,
			});

			if (userProfile.profilePicture) {
				const profilePicture = userProfile.profilePicture;
				const loadExistingProfilePicture = async () => {
					try {
						const signedUrl = await getPrivateFileUrl(profilePicture.key);
						setPreviewFile({
							name: profilePicture.name,
							bucket: profilePicture.bucket as "private" | "public",
							key: profilePicture.key,
							type: profilePicture.mimeType,
							size: 0,
							url: signedUrl,
						});
					} catch (error) {
						console.error(
							"Erreur lors du chargement de la photo de profil:",
							error,
						);
					}
				};
				loadExistingProfilePicture();
			}
		}
	}, [userProfile, profileForm]);

	const watchedProfilePicture = useWatch({
		control: profileForm.control,
		name: "profilePicture",
	});

	useEffect(() => {
		if (watchedProfilePicture?.url) {
			setPreviewFile(watchedProfilePicture);
		} else {
			setPreviewFile(undefined);
		}
	}, [watchedProfilePicture]);

	const handleLogout = () => {
		logout();
		navigate(PAGES.LOGIN);
	};

	const handleDeletePhoto = () => {
		profileForm.setValue("profilePicture", undefined);
		setPreviewFile(undefined);
		const formData = profileForm.getValues();
		updateProfileMutation.mutate(
			{
				firstname: formData.firstname,
				lastname: formData.lastname,
				email: formData.email,
				mobilePhone: formData.mobilePhone,
				profilePicture: undefined,
			},
			{
				onSuccess: () => {
					toast.success("Photo de profil supprimée avec succès");
				},
				onError: (
					error: Error & { response?: { data?: { message?: string } } },
				) => {
					const errorMessage =
						error?.response?.data?.message ||
						"Erreur lors de la suppression de la photo de profil";
					toast.error(errorMessage);
				},
			},
		);
	};

	const onSubmitProfile = profileForm.handleSubmit((data) => {
		updateProfileMutation.mutate(data);
	});

	const onSubmitPassword = passwordForm.handleSubmit((data) => {
		if (data.newPassword !== data.confirmPassword) {
			toast.error("Les mots de passe ne correspondent pas");
			return;
		}

		if (data.newPassword.length < 8) {
			toast.error("Le mot de passe doit contenir au moins 8 caractères");
			return;
		}

		changePasswordMutation.mutate(
			{
				oldPassword: data.currentPassword,
				newPassword: data.newPassword,
			},
			{
				onSuccess: () => {
					toast.success("Mot de passe mis à jour avec succès");
					passwordForm.reset();
				},
				onError: (
					error: Error & { response?: { data?: { message?: string } } },
				) => {
					const errorMessage =
						error?.response?.data?.message ||
						"Erreur lors de la mise à jour du mot de passe";
					toast.error(errorMessage);
				},
			},
		);
	});

	if (isLoading || !userProfile) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="text-lg">Chargement...</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="px-6 py-4 justify-between flex flex-row">
				<h1 className="text-2xl font-semibold">Mon compte</h1>
				<Button
					variant="outline"
					onClick={handleLogout}
					className="text-text-blue border-text-blue hover:bg-text-blue hover:text-white"
				>
					<LogOut04 className="w-4 h-4 mr-2" />
					Déconnexion
				</Button>
			</div>

			<div className="p-6">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
					<FormProvider {...profileForm}>
						<div className="space-y-6">
							<div className="text-xl font-semibold text-text-main-blue">
								Mon profil
							</div>
							<Card className="p-6 w-full">
								<div className="mb-6">
									<div className="text-sm font-medium text-gray-700 mb-2">
										Photo de profil
									</div>
									<div className="flex items-center space-x-4">
										{previewFile?.url ? (
											<div className="relative">
												<Avatar className="w-16 h-16">
													<AvatarImage
														src={previewFile.url}
														alt={previewFile.name ?? "Photo de profil"}
														className="object-cover"
													/>
													<AvatarFallback className="bg-text-main-blue text-white text-lg">
														{getInitials(
															userProfile?.firstname,
															userProfile?.lastname,
														)}
													</AvatarFallback>
												</Avatar>
												<Button
													variant="destructive"
													size="icon"
													className="absolute -top-2 -right-2 h-6 w-6"
													onClick={handleDeletePhoto}
												>
													<Trash02 className="h-4 w-4" />
												</Button>
											</div>
										) : (
											<Avatar className="w-16 h-16">
												<AvatarImage
													src="/placeholder-avatar.jpg"
													alt="Photo de profil"
												/>
												<AvatarFallback className="bg-text-main-blue text-white text-lg">
													{getInitials(
														userProfile?.firstname,
														userProfile?.lastname,
													)}
												</AvatarFallback>
											</Avatar>
										)}
										<div className="flex-1">
											<ControlledS3FileInput
												name="profilePicture"
												control={profileForm.control}
												bucket="private"
												onPreview={setPreviewFile}
												subText="SVG, PNG, JPG (max. 800x400px)"
												hidePreviewButton={true}
											/>
										</div>
									</div>
								</div>

								<div className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											label="Nom"
											name="lastname"
											control={profileForm.control}
										/>
										<ControlledInput
											label="Prénom"
											name="firstname"
											control={profileForm.control}
										/>
									</div>

									<div className="grid grid-cols-2 gap-4">
										<ControlledInput
											label="Email"
											name="email"
											control={profileForm.control}
											type="email"
										/>
										<ControlledPhoneInput
											label="Mobile"
											name="mobilePhone"
											control={profileForm.control}
										/>
									</div>

									<div className="flex justify-end space-x-3 pt-4">
										<Button
											type="button"
											variant="outline"
											onClick={() => profileForm.reset()}
											disabled={updateProfileMutation.isPending}
										>
											Annuler
										</Button>
										<Button
											type="button"
											onClick={onSubmitProfile}
											disabled={updateProfileMutation.isPending}
										>
											{updateProfileMutation.isPending
												? "Enregistrement..."
												: "Enregistrer les modifications"}
										</Button>
									</div>
								</div>
							</Card>
						</div>
					</FormProvider>

					<FormProvider {...passwordForm}>
						<div className="space-y-6">
							<div className="text-xl font-semibold text-text-main-blue">
								Mot de passe
							</div>

							<Card className="p-6">
								<div className="space-y-4">
									<ControlledPassword
										label="Mot de passe actuel"
										name="currentPassword"
										control={passwordForm.control}
										placeholder=" "
										error={
											passwordForm.formState.errors.currentPassword?.message
										}
									/>

									<ControlledPassword
										label="Nouveau mot de passe"
										name="newPassword"
										control={passwordForm.control}
										placeholder=""
										error={passwordForm.formState.errors.newPassword?.message}
									/>

									<ControlledPassword
										label="Confirmation du nouveau mot de passe"
										name="confirmPassword"
										control={passwordForm.control}
										placeholder=""
										error={
											passwordForm.formState.errors.confirmPassword?.message
										}
									/>

									<div className="flex justify-end space-x-3 pt-4">
										<Button
											type="button"
											variant="outline"
											onClick={() => passwordForm.reset()}
											disabled={changePasswordMutation.isPending}
										>
											Annuler
										</Button>
										<Button
											type="button"
											onClick={onSubmitPassword}
											disabled={changePasswordMutation.isPending}
										>
											{changePasswordMutation.isPending
												? "Mise à jour..."
												: "Mettre à jour le mot de passe"}
										</Button>
									</div>
								</div>
							</Card>
						</div>
					</FormProvider>
				</div>
			</div>
		</div>
	);
};

export default ProfilePage;
